import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/services/api'
import websocket from '@/services/websocket'
import { applyTheme } from '@/utils/theme'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    token: localStorage.getItem('token') || null,
    persons: [],
    currentPerson: null,
    records: [],
    currentRecord: null,
    shares: [],
    loading: false,
    error: null
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
      localStorage.setItem('user', JSON.stringify(user))
      // 登录返回的 user.theme 是账号里保存的主题，登录后自动应用
      if (user && user.theme) {
        applyTheme(user.theme)
      }
    },
    SET_TOKEN(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    LOGOUT(state) {
      // 清空登录态
      state.user = null
      state.token = null
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      // 清空所有与当前用户相关的业务数据，避免退出后残留导致下一个登录用户串号
      state.persons = []
      state.currentPerson = null
      state.records = []
      state.currentRecord = null
      state.shares = []
      state.loading = false
      state.error = null
    },
    SET_PERSONS(state, persons) {
      state.persons = persons
    },
    ADD_PERSON(state, person) {
      state.persons.push(person)
    },
    UPDATE_PERSON(state, person) {
      const index = state.persons.findIndex(p => p.id === person.id)
      if (index !== -1) {
        state.persons.splice(index, 1, person)
      }
    },
    DELETE_PERSON(state, personId) {
      state.persons = state.persons.filter(p => p.id !== personId)
    },
    SET_CURRENT_PERSON(state, person) {
      state.currentPerson = person
    },
    SET_RECORDS(state, records) {
      state.records = records
    },
    ADD_RECORD(state, record) {
      state.records.push(record)
    },
    UPDATE_RECORD(state, record) {
      const index = state.records.findIndex(r => r.id === record.id)
      if (index !== -1) {
        state.records.splice(index, 1, record)
      }
    },
    DELETE_RECORD(state, recordId) {
      state.records = state.records.filter(r => r.id !== recordId)
    },
    SET_CURRENT_RECORD(state, record) {
      state.currentRecord = record
    },
    SET_SHARES(state, shares) {
      state.shares = shares
    },
    ADD_SHARE(state, share) {
      state.shares.push(share)
    },
    UPDATE_SHARE(state, share) {
      const index = state.shares.findIndex(s => s.id === share.id)
      if (index !== -1) {
        state.shares.splice(index, 1, share)
      }
    },
    DELETE_SHARE(state, shareId) {
      state.shares = state.shares.filter(s => s.id !== shareId)
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    CLEAR_ERROR(state) {
      state.error = null
    }
  },
  actions: {
    async login({ commit }, credentials) {
      const response = await api.user.login(credentials)
      if (response.data) {
        commit('SET_USER', response.data.user)
        commit('SET_TOKEN', response.data.token)
        return response.data
      }
    },
    async register({ commit }, userData) {
      const response = await api.user.register(userData)
      if (response.data) {
        commit('SET_USER', response.data.user)
        commit('SET_TOKEN', response.data.token)
        return response.data
      }
    },
    async loginByPhone({ commit }, phoneData) {
      const response = await api.user.loginByPhone(phoneData)
      if (response.data) {
        commit('SET_USER', response.data.user)
        commit('SET_TOKEN', response.data.token)
        return response.data
      }
    },
    // 微信扫码登录：传入微信回调返回的 code
    async loginByWechat({ commit }, code) {
      const response = await api.user.loginByWechat(code)
      if (response.data && response.data.success) {
        commit('SET_USER', response.data.user)
        commit('SET_TOKEN', response.data.token)
        return response.data
      }
      // 失败时抛出便于页面捕获提示
      throw new Error(response.data && response.data.message ? response.data.message : '微信登录失败')
    },
    logout({ commit }) {
      commit('LOGOUT')
      // 退出登录时断开 WebSocket 连接，避免旧连接残留影响下一个登录用户
      websocket.disconnect()
    },
    
    // 人物相关操作
    setPersons({ commit }, persons) {
      commit('SET_PERSONS', persons)
    },
    addPerson({ commit }, person) {
      commit('ADD_PERSON', person)
    },
    updatePerson({ commit }, person) {
      commit('UPDATE_PERSON', person)
    },
    deletePerson({ commit }, personId) {
      commit('DELETE_PERSON', personId)
    },
    setCurrentPerson({ commit }, person) {
      commit('SET_CURRENT_PERSON', person)
    },
    
    // 记录相关操作
    setRecords({ commit }, records) {
      commit('SET_RECORDS', records)
    },
    addRecord({ commit }, record) {
      commit('ADD_RECORD', record)
    },
    updateRecord({ commit }, record) {
      commit('UPDATE_RECORD', record)
    },
    deleteRecord({ commit }, recordId) {
      commit('DELETE_RECORD', recordId)
    },
    setCurrentRecord({ commit }, record) {
      commit('SET_CURRENT_RECORD', record)
    },
    
    // 分享相关操作
    setShares({ commit }, shares) {
      commit('SET_SHARES', shares)
    },
    addShare({ commit }, share) {
      commit('ADD_SHARE', share)
    },
    updateShare({ commit }, share) {
      commit('UPDATE_SHARE', share)
    },
    deleteShare({ commit }, shareId) {
      commit('DELETE_SHARE', shareId)
    },
    
    // 加载状态和错误处理
    setLoading({ commit }, loading) {
      commit('SET_LOADING', loading)
    },
    setError({ commit }, error) {
      commit('SET_ERROR', error)
    },
    clearError({ commit }) {
      commit('CLEAR_ERROR')
    }
  },
  getters: {
    isLoggedIn: state => !!state.user && !!state.token,
    currentUser: state => state.user,
    token: state => state.token,
    allPersons: state => state.persons,
    currentPerson: state => state.currentPerson,
    allRecords: state => state.records,
    currentRecord: state => state.currentRecord,
    allShares: state => state.shares,
    isLoading: state => state.loading,
    error: state => state.error
  }
})
